import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import YearSelector from '../components/gallery/YearSelector';
import EventSelector from '../components/gallery/EventSelector';
import MediaTabs from '../components/gallery/MediaTabs';
import MediaGrid from '../components/gallery/MediaGrid';
import MediaLightbox from '../components/gallery/MediaLightbox';
import './GalleryPage.css';

// Prevent download behaviors (right-click, drag) globally on the gallery page
const preventDownload = (e) => {
  e.preventDefault();
};
export default function GalleryPage() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('photos');
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxItems, setLightboxItems] = useState([]);

  // Load available years on mount
  useEffect(() => {
    setLoadingYears(true);
    api
      .get('/api/gallery/years')
      .then((res) => {
        const yrs = res.data?.years || [];
        setYears(yrs);
        if (yrs.length > 0) setSelectedYear(yrs[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingYears(false));
  }, []);

  // Load events for selected year
  useEffect(() => {
    if (!selectedYear) return;
    setLoadingEvents(true);
    api
      .get(`/api/gallery/by-year/${selectedYear}`)
      .then((res) => {
        const events = res.data?.events || [];
        // Sort events by date in descending order (newest first)
        const sortedEvents = events.sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
        setEvents(sortedEvents);
        setSelectedEvent(sortedEvents[0] || null);
        setActiveTab('photos');
      })
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [selectedYear]);

  const handleEventSelect = useCallback((event) => {
    setSelectedEvent(event);
    setActiveTab('photos');
  }, []);

  const currentItems = selectedEvent
    ? activeTab === 'photos'
      ? selectedEvent.photos
      : selectedEvent.videos
    : [];

  const handleItemClick = (item, index) => {
    setLightboxItems(currentItems);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const lightboxClose = () => setLightboxOpen(false);
  const lightboxPrev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : lightboxItems.length - 1));
  const lightboxNext = () =>
    setLightboxIndex((i) => (i < lightboxItems.length - 1 ? i + 1 : 0));

  if (loadingYears) {
    return (
      <div className="gallery-page">
        <div className="gallery-page-layout">
          <aside className="gallery-page-sidebar">
            <nav className="gallery-year-selector" aria-label="Years">
              <div className="skeleton skeleton-title" style={{ height: '2rem', width: '60px' }} />
            </nav>
          </aside>
          <main className="gallery-page-main">
            <div className="gallery-event-selector">
              <div className="gallery-event-card-skeleton" />
              <div className="gallery-event-card-skeleton" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-page" onContextMenu={preventDownload} onDragStart={preventDownload}>
      <header className="gallery-page-header">
        <h1>Gallery</h1>
        <p>Explore photos and videos from our events</p>
      </header>

      <div className="gallery-page-layout">
        {/* Sidebar: Year selector */}
        <aside className="gallery-page-sidebar">
          <YearSelector years={years} currentYear={selectedYear} onChange={setSelectedYear} />
        </aside>

        {/* Main area */}
        <main className="gallery-page-main">
          {years.length === 0 ? (
            <div className="empty-state-hero">
              <h2>No gallery data available</h2>
              <p>There are no events or photos in the system yet. Once events are created, the gallery will populate with photos and videos from those events.</p>
            </div>
          ) : loadingEvents ? (
            <div className="gallery-event-selector">
              <div className="gallery-event-card-skeleton" />
              <div className="gallery-event-card-skeleton" />
              <div className="gallery-event-card-skeleton" />
            </div>
          ) : !events.length ? (
            <div className="gallery-event-selector-empty">
              <span style={{ fontSize: '2.5rem' }}>📅</span>
              <p>No events found for this year.</p>
            </div>
          ) : (
            <>
              {/* Event selector cards */}
              <EventSelector events={events} selectedId={selectedEvent?.event_id || selectedEvent?.event_name} onSelect={handleEventSelect} />

              {/* Event detail + media grid */}
              {selectedEvent && (
                <div className="gallery-event-detail" style={{ marginTop: '1.5rem' }}>
                  <div className="gallery-event-detail-header">
                    <h2>{selectedEvent.event_name}</h2>
                    {selectedEvent.event_date && (
                      <time dateTime={selectedEvent.event_date}>
                        {new Date(selectedEvent.event_date).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </time>
                    )}
                  </div>

                  <MediaTabs activeTab={activeTab} onChange={setActiveTab} photoCount={selectedEvent.photos?.length || 0} videoCount={selectedEvent.videos?.length || 0} />

                  <MediaGrid items={currentItems} onItemClick={handleItemClick} loading={false} />
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <MediaLightbox items={lightboxItems} currentIndex={lightboxIndex} onClose={lightboxClose} onPrev={lightboxPrev} onNext={lightboxNext} />
      )}

      {/* Copyright Footer */}
      <footer className="gallery-page-footer">
        <p>Copyright: All content on this site is the property of Vedanta Center of Charlotte, Inc. Unauthorized duplication, distribution, or modification of this material is strictly prohibited.</p>
      </footer>
    </div>
  );
}
